import classNames from 'classnames';
import PropTypes from 'prop-types';
import React, { useContext, useEffect, useState } from 'react';
import { TIMER_RESERVATION } from '../../brands/default/content/components/checkout';
import { getTimerObject } from '../../helpers/timer';
import ThemeContext from '../../providers/ThemeProvider';
import useToast from '../_generic/toast/UseToast';

export default function TimerReservation({
  banner,
  endDateTimestamp,
  onCountdownFinish,
  size = 'md',
  timerPopup,
}) {
  const theme = useContext(ThemeContext);
  const [remaining, setRemaining] = useState(getTimerObject(endDateTimestamp));
  const toast = useToast();
  const COMPONENT_LITERAL = '<span class="digit">$1</span>';

  useEffect(() => {
    if (endDateTimestamp) {
      const interval = setInterval(() => {
        const newRemaining = getTimerObject(endDateTimestamp);
        setRemaining(newRemaining);
        if (
          newRemaining &&
          newRemaining.seconds &&
          newRemaining.seconds === '00' &&
          newRemaining.minutes &&
          newRemaining.minutes === '00'
        ) {
          clearInterval(interval);
          toast.addToast(
            TIMER_RESERVATION.reservedEndMessage,
            'toast-info',
            'top-right',
          );
          // call page refresh
          setTimeout(() => {
            // wait a few sec
            onCountdownFinish();
          }, 3_000);
        }
      }, 1_000);

      return () => clearInterval(interval);
    }

    return null;
  }, [endDateTimestamp]);

  // date was not in future, or counted down to 0
  if (
    !remaining ||
    (remaining && remaining.minutes === '00' && remaining.seconds === '00')
  )
    return null;

  return (
    <>
      {/* Added styling for both checkout timer and popup timer */}
      <div
        className={classNames('timer', {
          'timer-banner': banner,
          'timer-popup': timerPopup && !banner,
        })}
      >
        <span
          className={classNames('timer-message', {
            'timer-popup__message': timerPopup,
          })}
        >
          {TIMER_RESERVATION.reservedMessage}:
        </span>

        <span
          className={classNames('timer-time', {
            'timer-time--lg': size === 'lg',
            'timer-time--md': size === 'md',
          })}
        >
          {remaining.days > 0 && (
            <>
              <span
                dangerouslySetInnerHTML={{
                  __html: remaining.days.replace(/(\$?\d)/g, COMPONENT_LITERAL),
                }}
              />
              <span>:</span>
            </>
          )}
          {remaining.hours > 0 && (
            <>
              <span
                dangerouslySetInnerHTML={{
                  __html: remaining.hours.replace(
                    /(\$?\d)/g,
                    COMPONENT_LITERAL,
                  ),
                }}
              />
              :
            </>
          )}
          <span
            dangerouslySetInnerHTML={{
              __html: remaining.minutes.replace(/(\$?\d)/g, COMPONENT_LITERAL),
            }}
          />
          :
          <span
            dangerouslySetInnerHTML={{
              __html: remaining.seconds.replace(/(\$?\d)/g, COMPONENT_LITERAL),
            }}
          />
        </span>
      </div>
      <style jsx>{`
        .timer {
          max-width: 325px;
          display: flex;
          flex-direction: row;
          align-items: center;
          justify-content: center;
          padding: 0px 16px;
          border-right: 1px solid #ccc;
        }
        .timer-popup {
          display: flex;
          flex-direction: column;
          text-align: center;
          margin: 0 auto;
          gap: 1.15rem;
          padding-bottom: 4rem;
          border-right: none;
        }
        .timer-banner {
          display: none;
          height: 4rem;
          max-width: 100%;
          border-right: none;
          border-top: 1px solid #6aac22;
          border-bottom: 1px solid #6aac22;
          background-color: #e2f0d9;
        }
        .timer-message {
          color: ${theme.commoncolors.greycharcoal};
          font-size: 13px;
          padding-right: 4px;
        }
        .timer-banner .timer__text {
          font-size: 14px;
        }

        .timer-popup__message {
          font-size: 20px;
          font-weight: bold;
        }
        .timer-time {
          color: ${theme.colors.primary};
        }
        .timer-time--md,
        .timer-time--md :global(.digit) {
          font-size: 21px;
        }
        .timer-time--lg,
        .timer-time--lg :global(.digit) {
          font-size: 26px;
        }
        :global(.digit) {
          background-color: ${theme.commoncolors.white};
          border-radius: 6px;
          box-shadow: rgb(0, 0, 0, 0.1) 3px 3px 6px 0px inset,
            rgba(0, 0, 0, 0.05) -2px -2px 6px 1.25px inset;
          font-weight: bold;
          margin: 0 2px;
          padding: 2px 4.5px;
        }
        @media (min-width: ${theme.breakpoints.lgUp}) {
          .timer-popup {
            margin: auto;
          }
        }
        @media (min-width: ${theme.breakpoints.xlUp}) {
          .timer {
            margin-right: 20px;
          }
          .timer-popup {
            margin: auto;
          }
        }
        @media (max-width: ${theme.breakpoints.lgDown}) {
          .timer {
            display: none;
          }
          .timer-popup {
            display: flex;
          }
          .timer-banner {
            display: flex;
          }
          .timer-time--md,
          .timer-time--md :global(.digit) {
            font-size: 18px;
          }
          .timer-time--lg,
          .timer-time--lg :global(.digit) {
            font-size: 24px;
          }
        }
      `}</style>
    </>
  );
}

TimerReservation.propTypes = {
  banner: PropTypes.bool,
  endDateTimestamp: PropTypes.number,
  onCountdownFinish: PropTypes.func,
  size: PropTypes.string,
  timerPopup: PropTypes.bool,
};
